export const CONTENT = {
  en: {
    nav: {
      story: "The story",
      gallery: "Pieces",
      commission: "Create together",
    },
    hero: {
      eyebrow: "An atelier of found things — Norway",
      lines: ["Turning memory into", "something you can keep."],
      sub: "One-of-a-kind keepsakes, made from the objects you cannot let go of.",
      ctaPrimary: "Create together",
      ctaSecondary: "Read the story",
      scroll: "Scroll",
      imageAlt: "Handcrafted wall hanging of wooden hoops, antler and driftwood in warm afternoon light",
    },
    story: {
      eyebrow: "The story",
      heading: "Every object holds a story",
      s1: "The Things We Keep is a creative studio built around memory, meaning and the beauty of what remains.",
      s2: "I transform objects, materials and fragments with a story into one-of-a-kind pieces for the home — preserving what mattered while giving it a new form.",
      imageCaption: "From the studio wall — a detail, mid-knot.",
      pull: "Not everything we keep belongs in a box.",
      pullSub: "Found. Gathered. Kept.",
      s3: "Some things deserve to become part of the life we are still living.",
    },
    marquee: ["found", "gathered", "kept", "nature", "history", "art", "home"],
    how: {
      eyebrow: "How it works",
      heading: "Three quiet steps",
      steps: [
        {
          n: "01",
          t: "Share your object",
          d: "Tell me what it is, and why it matters. A letter, a conversation, a photograph — whatever feels right.",
        },
        {
          n: "02",
          t: "I transform it",
          d: "Together we choose the form: a piece for the wall, a textile, an object to hold. Then my hands do the quiet work.",
        },
        {
          n: "03",
          t: "You keep the memory",
          d: "Your piece arrives ready to live with you — and to be passed on, one day.",
        },
      ],
    },
    gallery: {
      eyebrow: "Past pieces",
      heading: "Each one, a memory given form",
      sub: "One of a kind, never repeated — made from the objects and materials you send me.",
      empty: "The first pieces are being photographed. Come back soon.",
      loading: "Gathering the pieces…",
    },
    commission: {
      eyebrow: "Commissions",
      heading: "Start with the story",
      sub: "Every commission starts with a conversation. From there, I will suggest how we might work together and what the project may cost. There is no obligation to continue.",
      lede: "Tell me what you are carrying. It might be an object, a memory, a person, a place, a beginning or an ending. You don't need to know what it should become. That's where we begin.",
      contactLine: "Prefer to write directly?",
      fieldName: "Your name",
      fieldEmail: "Email",
      fieldStory: "The story of your object",
      storyPlaceholder: "It belonged to…",
      fieldPhoto: "A photograph, if you have one",
      optional: "optional",
      submit: "Send the story",
      sending: "Sending…",
      success: "Thank you. Your story is on its way — I will write back soon.",
      successSub: "Until then, keep it somewhere safe.",
      error: "Something went wrong. Please try again, or write to me directly.",
      photoHelp: "JPG or PNG, up to 8 MB. Large photos are made smaller automatically.",
    },
    signup: {
      heading: "Not ready yet? Stay in touch.",
      sub: "One letter, now and then, from the atelier. No noise.",
      placeholder: "Your email",
      button: "Sign up",
      sending: "Signing up…",
      success: "You're on the list.",
      error: "Please try again in a moment.",
    },
    footer: {
      tagline: "Memory, given form.",
      made: "Created by Carine Schackt in Norway",
      unique: "Every piece exists only once.",
      write: "Write to me",
    },
  },
  no: {
    nav: {
      story: "Historien",
      gallery: "Verk",
      commission: "Start en bestilling",
    },
    hero: {
      eyebrow: "Et atelier for funn — Norge",
      lines: ["Å forvandle minner til", "noe du kan ta vare på."],
      sub: "Unike minner, laget av gjenstandene du ikke klarer å slippe.",
      ctaPrimary: "Start en bestilling",
      ctaSecondary: "Les historien",
      scroll: "Bla nedover",
      imageAlt: "Håndlaget veggkunst av tverringer, gevir og drivved i varmt ettermiddagslys",
    },
    story: {
      eyebrow: "Historien",
      heading: "Enhver gjenstand bærer en historie",
      s1: "The Things We Keep er et kreativt studio bygget rundt minner, mening og skjønnheten i det som blir igjen.",
      s2: "Jeg forvandler gjenstander, materialer og fragmenter med en historie til unike verk for hjemmet — jeg tar vare på det som betydde noe, og gir det en ny form.",
      imageCaption: "Fra atelierveggen — en detalj, midt i en knute.",
      pull: "Ikke alt vi tar vare på hører hjemme i en eske.",
      pullSub: "Funn. Saml. Skap.",
      s3: "Noen ting fortjener å bli en del av livet vi fortsatt lever.",
    },
    marquee: ["funn", "saml", "skap", "natur", "historie", "kunst", "hjem"],
    how: {
      eyebrow: "Slik fungerer det",
      heading: "Tre stille steg",
      steps: [
        {
          n: "01",
          t: "Del gjenstanden din",
          d: "Fortell meg hva det er, og hvorfor det betyr noe. Et brev, en samtale, et fotografi — hva enn som føles riktig.",
        },
        {
          n: "02",
          t: "Jeg forvandler den",
          d: "Sammen velger vi formen: et verk for veggen, et tekstil, en gjenstand å holde. Deretter gjør hendene mine det stille arbeidet.",
        },
        {
          n: "03",
          t: "Du beholder minnet",
          d: "Verket ditt kommer klar til å leve med deg — og til å gis videre, en dag.",
        },
      ],
    },
    gallery: {
      eyebrow: "Tidligere verk",
      heading: "Hvert verk, et minne gitt form",
      sub: "Unikt og aldri gjentatt — laget av gjenstandene og materialene du sender meg.",
      empty: "De første verkene blir fotografert akkurat nå. Kom snart tilbake.",
      loading: "Samler verkene…",
    },
    commission: {
      eyebrow: "Bestillinger",
      heading: "Begynn med historien",
      sub: "Hver bestilling begynner med en samtale. Deretter foreslår jeg hvordan vi kan jobbe sammen, og hva prosjektet kan koste. Du er helt uforpliktet.",
      lede: "Fortell meg hva du bærer på. Det kan være en gjenstand, et minne, et menneske, et sted, en begynnelse eller en slutt. Du trenger ikke å vite hva det skal bli. Der begynner vi.",
      contactLine: "Foretrekker du å skrive direkte?",
      fieldName: "Navnet ditt",
      fieldEmail: "E-post",
      fieldStory: "Historien om gjenstanden din",
      storyPlaceholder: "Den tilhørte…",
      fieldPhoto: "Et fotografi, hvis du har ett",
      optional: "valgfritt",
      submit: "Send historien",
      sending: "Sender…",
      success: "Takk. Historien din er på vei — jeg skriver snart tilbake.",
      successSub: "Inntil da — ta godt vare på den.",
      error: "Noe gikk galt. Prøv igjen, eller skriv til meg direkte.",
      photoHelp: "JPG eller PNG, inntil 8 MB. Store bilder gjøres mindre automatisk.",
    },
    signup: {
      heading: "Ikke klar ennå? Hold kontakten.",
      sub: "Ett brev av og til, fra atelieret. Ingen støy.",
      placeholder: "Din e-post",
      button: "Meld på",
      sending: "Melder på…",
      success: "Du er på listen.",
      error: "Prøv igjen om et øyeblikk.",
    },
    footer: {
      tagline: "Minner, gitt form.",
      made: "Skapt av Carine Schackt i Norge",
      unique: "Hvert stykke finnes bare én gang.",
      write: "Skriv til meg",
    },
  },
};
